import { useState, useEffect, useCallback } from 'react';
import {
  X,
  Upload,
  Search,
  Trash2,
  Plus,
  Folder,
  User,
  Palette,
  ImageIcon,
  Loader2,
  Check,
} from 'lucide-react';
import { useStore } from '../store';
import { imageApi } from '../services/imageApi';
import type { StyleReference, StyleReferenceUpload } from '../types/images';

interface StyleLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  selectionMode?: boolean;
  onSelect?: (refs: StyleReference[]) => void;
}

const CATEGORIES = [
  { id: 'all', label: 'All', icon: Folder },
  { id: 'style', label: 'Styles', icon: Palette },
  { id: 'character', label: 'Characters', icon: User },
  { id: 'branding', label: 'Branding', icon: ImageIcon },
  { id: 'scene', label: 'Scenes', icon: ImageIcon },
] as const;

export function StyleLibrary({
  isOpen,
  onClose,
  selectionMode = false,
  onSelect,
}: StyleLibraryProps) {
  const {
    styleReferences,
    setStyleReferences,
    addStyleReference,
    removeStyleReference,
    addNotification,
  } = useStore();

  const [isLoading, setIsLoading] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedRefs, setSelectedRefs] = useState<Set<string>>(new Set());
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadData, setUploadData] = useState({
    name: '',
    description: '',
    category: 'style' as StyleReference['category'],
    tags: '',
  });
  const [isUploading, setIsUploading] = useState(false);

  const loadReferences = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await imageApi.getStyleReferences(
        selectedCategory === 'all' ? undefined : selectedCategory
      );
      if (result?.references) {
        setStyleReferences(result.references);
      }
    } catch (err) {
      // Silently fail - tables may not exist yet
      console.warn('Failed to load style references:', err);
      setStyleReferences([]);
    } finally {
      setIsLoading(false);
    }
  }, [selectedCategory, setStyleReferences]);

  useEffect(() => {
    if (isOpen) {
      loadReferences();
    }
  }, [isOpen, loadReferences]);

  const filteredReferences = styleReferences.filter((ref) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        ref.name.toLowerCase().includes(query) ||
        ref.description?.toLowerCase().includes(query) ||
        ref.tags?.some((t) => t.toLowerCase().includes(query))
      );
    }
    return true;
  });

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadFile(file);
      setUploadData((prev) => ({ ...prev, name: file.name.split('.')[0] }));

      const reader = new FileReader();
      reader.onload = () => setUploadPreview(reader.result as string);
      reader.readAsDataURL(file);
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadData.name) return;

    setIsUploading(true);
    try {
      const upload: StyleReferenceUpload = {
        file: uploadFile,
        name: uploadData.name,
        description: uploadData.description || undefined,
        category: uploadData.category,
        tags: uploadData.tags
          .split(',')
          .map((t) => t.trim())
          .filter(Boolean),
      };

      const result = await imageApi.uploadStyleReference(upload);
      if (result.success) {
        addStyleReference(result.reference);
        addNotification('Style reference uploaded', 'success');
        resetUploadForm();
      }
    } catch (err) {
      addNotification('Failed to upload style reference', 'error');
    } finally {
      setIsUploading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await imageApi.deleteStyleReference(id);
      removeStyleReference(id);
      addNotification('Style reference deleted', 'success');
    } catch (err) {
      addNotification('Failed to delete style reference', 'error');
    }
  };

  const resetUploadForm = () => {
    setShowUploadModal(false);
    setUploadFile(null);
    setUploadPreview(null);
    setUploadData({
      name: '',
      description: '',
      category: 'style',
      tags: '',
    });
  };

  const toggleSelection = (id: string) => {
    const newSelection = new Set(selectedRefs);
    if (newSelection.has(id)) {
      newSelection.delete(id);
    } else {
      newSelection.add(id);
    }
    setSelectedRefs(newSelection);
  };

  const handleConfirmSelection = () => {
    if (onSelect) {
      const selected = styleReferences.filter((r) => selectedRefs.has(r.id));
      onSelect(selected);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-accent/20 rounded-lg">
              <Palette className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h2 className="text-xl font-semibold">Style Library</h2>
              <p className="text-sm text-gray-500">
                {selectionMode
                  ? 'Select references for consistent image generation'
                  : 'Manage your style and character references'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Toolbar */}
        <div className="px-6 py-3 border-b flex items-center justify-between gap-4">
          {/* Categories */}
          <div className="flex gap-2">
            {CATEGORIES.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setSelectedCategory(id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  selectedCategory === id
                    ? 'bg-accent/20 text-accent-dark'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
              >
                <Icon className="w-4 h-4" />
                {label}
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search references..."
              className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-accent focus:border-transparent"
            />
          </div>

          {/* Upload Button */}
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark transition-colors"
          >
            <Plus className="w-4 h-4" />
            Upload
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="w-8 h-8 text-accent animate-spin" />
            </div>
          ) : filteredReferences.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <ImageIcon className="w-16 h-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No references yet</h3>
              <p className="text-sm text-gray-400 mb-4">
                Upload images to maintain consistent styles and characters
              </p>
              <button
                onClick={() => setShowUploadModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark"
              >
                <Upload className="w-4 h-4" />
                Upload Your First Reference
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
              {filteredReferences.map((ref) => {
                const isSelected = selectedRefs.has(ref.id);
                return (
                  <div
                    key={ref.id}
                    className={`group relative rounded-xl overflow-hidden border-2 transition-all cursor-pointer ${
                      isSelected
                        ? 'border-accent ring-2 ring-accent/30'
                        : 'border-transparent hover:border-gray-200'
                    }`}
                    onClick={() => selectionMode && toggleSelection(ref.id)}
                  >
                    <div className="aspect-square">
                      <img
                        src={ref.thumbnailUrl}
                        alt={ref.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    {/* Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-0 left-0 right-0 p-3">
                        <h4 className="text-sm font-medium text-white truncate">
                          {ref.name}
                        </h4>
                        <p className="text-xs text-gray-300 capitalize">{ref.category}</p>
                      </div>
                    </div>

                    {/* Selection Checkbox */}
                    {selectionMode && (
                      <div
                        className={`absolute top-2 right-2 w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                          isSelected
                            ? 'bg-accent text-white'
                            : 'bg-white/80 text-transparent group-hover:text-gray-400'
                        }`}
                      >
                        <Check className="w-4 h-4" />
                      </div>
                    )}

                    {/* Delete Button (non-selection mode) */}
                    {!selectionMode && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(ref.id);
                        }}
                        className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-lg opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}

                    {/* Category Badge */}
                    <div className="absolute top-2 left-2">
                      <span className="px-2 py-0.5 bg-black/50 text-white text-xs rounded-full capitalize">
                        {ref.category}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        {selectionMode && (
          <div className="px-6 py-4 border-t bg-gray-50 flex justify-between items-center">
            <p className="text-sm text-gray-500">
              {selectedRefs.size} reference{selectedRefs.size !== 1 ? 's' : ''} selected
            </p>
            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-white"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmSelection}
                disabled={selectedRefs.size === 0}
                className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark disabled:opacity-50"
              >
                Confirm Selection
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={resetUploadForm}>
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-4">Upload Style Reference</h3>

            {/* File Drop Zone */}
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 text-center mb-4 transition-colors ${
                uploadPreview
                  ? 'border-accent bg-accent/10'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
            >
              {uploadPreview ? (
                <div className="relative inline-block">
                  <img
                    src={uploadPreview}
                    alt="Preview"
                    className="max-h-40 rounded-lg mx-auto"
                  />
                  <button
                    onClick={() => {
                      setUploadFile(null);
                      setUploadPreview(null);
                    }}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ) : (
                <>
                  <Upload className="w-10 h-10 text-gray-400 mx-auto mb-2" />
                  <p className="text-sm text-gray-600 mb-2">
                    Drag and drop or click to upload
                  </p>
                  <p className="text-xs text-gray-400">PNG, JPG, WebP up to 10MB</p>
                </>
              )}
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
            </div>

            {/* Form Fields */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={uploadData.name}
                  onChange={(e) =>
                    setUploadData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent"
                  placeholder="e.g., Main Character - Emma"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Category *
                </label>
                <select
                  value={uploadData.category}
                  onChange={(e) =>
                    setUploadData((prev) => ({
                      ...prev,
                      category: e.target.value as StyleReference['category'],
                    }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent"
                >
                  <option value="style">Style</option>
                  <option value="character">Character</option>
                  <option value="branding">Branding</option>
                  <option value="scene">Scene</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={uploadData.description}
                  onChange={(e) =>
                    setUploadData((prev) => ({ ...prev, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg resize-none focus:ring-2 focus:ring-accent"
                  rows={2}
                  placeholder="Optional description..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tags
                </label>
                <input
                  type="text"
                  value={uploadData.tags}
                  onChange={(e) =>
                    setUploadData((prev) => ({ ...prev, tags: e.target.value }))
                  }
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-accent"
                  placeholder="e.g., fantasy, blue hair, young"
                />
                <p className="text-xs text-gray-400 mt-1">Separate tags with commas</p>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={resetUploadForm}
                className="px-4 py-2 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleUpload}
                disabled={!uploadFile || !uploadData.name || isUploading}
                className="flex items-center gap-2 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent-dark disabled:opacity-50"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Upload
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default StyleLibrary;
